import type { QueryClient } from '@tanstack/react-query';
import { AIRLINE_PROVIDER_VENDOR } from './airline-provider';
import type { IntegrationChange } from './types';

/**
 * `ws providers IntegrationChanged@v1` (B2) — someone's toggle, mode or routing
 * write landed. The event names WHAT moved, never the new row, so the honest
 * refresh is one re-read of the inventory (the screen's single query, active
 * only while Proveedores is open). Human-paced writes: no storm to fear, and no
 * timer anywhere — the push is the trigger (frontend-conventions §8.1).
 *
 * A remote console re-reads exactly what the local mutation re-reads: every
 * change moves the inventory; a routing save and a switch flip also move the
 * "Order of use" views (a switched vendor changes what each key SERVES).
 *
 * BURSTS COALESCE. One routing save emits one event PER MEMBER VENDOR (2–3 at
 * once). `cancelRefetch: false` makes every event after the first JOIN the read
 * already in flight instead of cancelling and restarting it — and since the
 * reads do not pass the abort signal, a restart would still send N requests.
 * The burst follows ONE committed write, so the joined read already sees it.
 */
const JOIN_IN_FLIGHT = { cancelRefetch: false } as const;

export const INTEGRATIONS_QUERY_KEY = ['providers', 'integrations'] as const;
/** §14.5 — the cache key of the "Order of use" views. */
export const ROUTING_QUERY_KEY = ['providers', 'routing'] as const;
/** The airline provider's card (`airline-air-ticketing.v1` §0). */
export const AIRLINE_PROVIDER_QUERY_KEY = ['providers', 'airline'] as const;

const CHANGES: readonly string[] = ['mode', 'toggle', 'routing'];

/** The change an envelope carries (flat per the seam; tolerates a `payload` wrapper). */
export function readIntegrationChange(event: object): IntegrationChange | null {
  const raw = event as Record<string, unknown>;
  const src = (typeof raw.vendor === 'string' ? raw : raw.payload) as Record<string, unknown> | undefined;
  if (!src || typeof src !== 'object') return null;
  if (typeof src.vendor !== 'string' || !CHANGES.includes(src.change as string)) return null;
  return src as unknown as IntegrationChange;
}

export function applyIntegrationChanged(qc: QueryClient, event: object): void {
  const change = readIntegrationChange(event);
  if (!change) return;
  void qc.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY, exact: true }, JOIN_IN_FLIGHT);
  if (change.change !== 'mode') {
    void qc.invalidateQueries({ queryKey: ROUTING_QUERY_KEY, exact: true }, JOIN_IN_FLIGHT);
  }
  // A switch of the airline provider moves what its card says it gives Nexa.
  if (change.change === 'toggle' && change.vendor === AIRLINE_PROVIDER_VENDOR) {
    void qc.invalidateQueries({ queryKey: AIRLINE_PROVIDER_QUERY_KEY, exact: true }, JOIN_IN_FLIGHT);
  }
}
